import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import BusinessBillList from 'components/Lists/BusinessBillList'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Container,
  InfoColumn,
  BillColumn,
  Row,
  SubTitle,
  InfoText,
  MoneyTextMask
} from './styles'

export default function CustomerServiceDetailsModal ({
  visible,
  customer,
  service,
  onClose: handleClose
}) {
  const { bundle: businessBundle, bill } = service
  const businessBundleService = businessBundle?.services.find (s => s.id === service.id)
  const servicePrice = businessBundleService.discount_price
  const [loading, setLoading] = useState (true)
  const [billBundle, setBillBundle] = useState (null)
  const [usedServiceBills, setUsedServiceBills] = useState ([])
  const [serviceDiscountPrice, setServiceDiscountPrice] = useState (0)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getData ()
  }, [visible])

  const getData = async () => {
    try {
      const { data: billBundles } = await cutileiApi.get (
        `/customers/${customer.id}/bundles?bundle_id=${businessBundle.id}`
        + (bill ? `&bill_id=${bill?.id}` : ''),
        requestConfig
      )

      const { data: usedServiceBills } = await cutileiApi.get (
        `/business_bills?business_id=${businessId}&customer_id=${customer.id}&deleted=false`
        + `&customer_services_id=${service.customer_services_id}&detailed=true`,
        requestConfig
      )

      setUsedServiceBills (usedServiceBills)
      setBillBundle (billBundles[0])
      const bundleDiscontPrice = billBundles[0]?.custom_price ?? businessBundle?.price

      setServiceDiscountPrice ((
        ((servicePrice * businessBundleService.amount) / businessBundle.price) * bundleDiscontPrice
      ) / businessBundleService.amount)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='xl' aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Detalhes do serviço</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? <Loading/> : (
            <Container>
              <Row style={{alignItems: 'flex-start'}}>
                <InfoColumn>
                  <Row>
                    <SubTitle>Nome:</SubTitle>
                    <InfoText>{service.name}</InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Pacote:</SubTitle>
                    <InfoText>{businessBundle.name}</InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Validade:</SubTitle>
                    <InfoText>
                      {service.expires_at
                        ? DateTime.fromISO (service.expires_at).toFormat ('dd/MM/yyyy')
                        : 'Indeterminado'
                      }
                    </InfoText>
                  </Row>
                  <Row>
                    <SubTitle>Preço original do pacote:</SubTitle>
                    <MoneyTextMask value={businessBundle?.price}/>
                  </Row>
                  <Row>
                    <SubTitle>Preço pago pelo pacote:</SubTitle>
                    {billBundle ? (
                      <MoneyTextMask value={billBundle?.custom_price}/>
                    ) : (
                      <MoneyTextMask value={businessBundle?.price}/>
                    )}
                  </Row>
                  <Row>
                    <SubTitle>Data da compra:</SubTitle>
                    <InfoText>
                      {service.created_at 
                        ? DateTime.fromISO (service.created_at).toFormat ('dd/MM/yyyy')
                        : '-'
                      }
                    </InfoText>
                  </Row>
                </InfoColumn>
                <InfoColumn>
                  <Row>
                    <SubTitle>Serviço s/ desconto:</SubTitle>
                    <MoneyTextMask value={servicePrice}/>
                  </Row>
                  <Row>
                    <SubTitle>Serviço c/ desconto:</SubTitle>
                    <MoneyTextMask value={serviceDiscountPrice}/>
                  </Row>
                  <Row>
                    <SubTitle>Desconto:</SubTitle>
                    <SubTitle>
                      {Math.round ((1 - serviceDiscountPrice / servicePrice) * 100)}%
                    </SubTitle>
                  </Row>
                  <Row>
                    <SubTitle>Qtd.:</SubTitle>
                    <SubTitle>{service.total_amount}</SubTitle>
                  </Row>
                  <Row>
                    <SubTitle>Qtd. utilizada:</SubTitle>
                    <SubTitle>{service.total_amount - service.available_amount}</SubTitle>
                  </Row>
                  <Row>
                    <SubTitle>Qtd. disponível:</SubTitle>
                    <SubTitle>{service.available_amount}</SubTitle>
                  </Row>
                </InfoColumn>
              </Row>
              {usedServiceBills.length > 0 && (
                <BillColumn>
                  <SubTitle style={{paddingBlock: 12}}>
                    Comandas dos serviços utilizados
                  </SubTitle>
                  <BusinessBillList
                    bills={usedServiceBills}
                    showTotalBills={false}
                    onEdit={bill => setUsedServiceBills (usedServiceBills.map (b => b.id === bill.id ? {...bill} : b))}
                    onDelete={bill => setUsedServiceBills (usedServiceBills.filter (b => b.id !== bill.id))}
                    containerStyle={{padding: 0}}
                  />
                </BillColumn>
              )}
            </Container>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
